import React, { useState, useContext, useEffect } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { useGetSeachParam } from "./useSearchParams";

import * as api from "./services";
import AppContext from "./appContext";
import useActiveContexts from "./Library/useActiveContexts";
import ConnectionLost from "./User/ConnectionLost";
import useAccountSettings from "./ManageAccount/useAccountSettings";
import JoinResearchDialog from "./JoinResearch/JoinResearchDialog";
import { logging } from "./tools";
import eventBus from "./eventBus";
import useErrorMessages from "./Home/useErrorMessages";

function Poll() {
  const {
    app: {
      client: {
        id: clientId,
        editable,
        lastSaved,
        unlocked,
        writeAccess,
        lockExpirationTime,
        lockMinutesLeft,
      },
      pauseQueries,
      project: { id: projectId },
      sessionId,
    },
    dispatch,
  } = useContext(AppContext);
  const { i18n } = useTranslation();
  const { accountSettings } = useAccountSettings();
  const [connectionLost, setConnectionLost] = useState({
    open: false,
    code: null,
  });
  const [openMailOnResearchDialog, setOpenMailOnResearchDialog] =
    useState(false);
  const clientIdPermalink = Number(useGetSeachParam("id"));

  const [lastChangedClientList, setLastChangedClientList] = useState(null); // timestamp of last changed client list
  const [lastChangedClients, setLastChangedClients] = useState({}); // object with key value pairs of last changed client (e.g., [clientId]: timestamp )
  const [userId, setUserId] = useState(null); // practitioner id
  const [lastChangedContexts, setLastChangedContexts] = useState(null); // practitioner id

  const queryClient = useQueryClient();
  const { headerCodes, refetchMessages } = useErrorMessages();

  const { refetchContexts } = useActiveContexts();

  const handleConnectionLost = (code) => {
    dispatch({ type: "PAUSE_QUERIES", payload: true }); // disabling/pausing queries
    setConnectionLost({ open: true, code: code }); // show the modal indicating the connection is lost
  };

  const updateLastChangedClient = (timestamp) => {
    setLastChangedClients((prev) => ({
      ...prev,
      [clientId]: timestamp,
    }));
  };

  const updateLastChangedClientList = (timestamp) => {
    setLastChangedClientList(timestamp);
  };

  const updateLastChangedContexts = (timestamp) => {
    setLastChangedContexts(timestamp);
  };

  // init the language and show the phone pair process if doOnDashboardLoad is welcomeWithTest
  useEffect(() => {
    if (accountSettings?.doOnDashboardLoad === "welcomeWithTest") {
      window.location = `${window.REACT_APP_ONBOARDING_ADDRESS}?sessionId=${sessionId}`; // show the phone pair process
    }
    if (accountSettings?.mailOnResearch == null) {
      // show the mailOnResearch message if mailOnResearch is not set
      setOpenMailOnResearchDialog(true);
    }
    // set the app in the correct language
    const lang = accountSettings?.lang ?? "en";
    i18n.changeLanguage(lang);
    moment.locale(lang); // display daynames in correct language

    // error occured in the services.js file
    eventBus.on("error", (code) => {
      refetchMessages(); // try to refetch the error messages
      handleConnectionLost(code); // pass the error code to the connection lost diagram
    });

    return () => {
      eventBus.off("error");
    };
  }, [accountSettings]);

  // Permalinks: init the client at startup
  useQuery(
    ["client", [clientIdPermalink, projectId]],
    () => api.poll({ id: clientIdPermalink, projectId, sessionId }),
    {
      enabled: Boolean(clientIdPermalink) && !pauseQueries,
      onSuccess: (data) => {
        if (data.client) {
          logging(`Set client with id ${clientIdPermalink}`);
          dispatch({
            type: "SET_CLIENT",
            payload: data.client,
          });
        }
      },
    }
  );

  // The query will execute every 3 seconds
  useQuery(
    ["poll", projectId],
    () => api.poll({ id: clientId, projectId, sessionId }),
    {
      refetchInterval: 3000,
      enabled: !pauseQueries,
      onSuccess: (data) => {
        // signed in: poll for change
        if (data.status === 1) {
          // who is logged in?
          if (!userId) {
            // INIT userid
            setUserId(data.userId);
          } else if (userId !== data.userId) {
            // other user is logged in
            handleConnectionLost(-1);
            return;
          }

          // poll for contexts
          if (!lastChangedContexts) {
            // INIT
            updateLastChangedContexts(data.lastChanged_connectionspro);
          } else if (lastChangedContexts !== data.lastChanged_connectionspro) {
            updateLastChangedContexts(data.lastChanged_connectionspro);
            refetchContexts(); // UPDATE contexts
          }

          // poll for client list
          if (!lastChangedClientList) {
            // INIT
            updateLastChangedClientList(data.lastChanged_connections);
          } else if (lastChangedClientList !== data.lastChanged_connections) {
            // UPDATE client list
            updateLastChangedClientList(data.lastChanged_connections);
            dispatch({
              type: "UPDATE_CLIENTS",
              payload: data.lastChanged_connections,
            }); // trigger clients list to refresh
          }

          // poll for client
          if (!lastChangedClients[clientId]) {
            // INIT
            updateLastChangedClient(data.lastChanged_client);
          } else if (
            data.lastChanged_client &&
            lastChangedClients[clientId] !== data.lastChanged_client
          ) {
            // UPDATE
            updateLastChangedClient(data.lastChanged_client);
            queryClient.invalidateQueries(["timeline", clientId]);
            dispatch({
              type: "SET_CLIENT",
              payload: data.client,
            });
          }

          if (
            data?.lock?.connectionId === clientId &&
            (data?.lock?.editable !== editable ||
              data?.lock?.unlocked !== unlocked ||
              data?.lock?.lastSaved !== lastSaved ||
              data?.lock?.lockExpirationTime !== lockExpirationTime ||
              (data?.lock?.lockMinutesLeft !== lockMinutesLeft &&
                data?.lock?.lockMinutesLeft >= 0) ||
              data?.lock?.writeAccess !== writeAccess)
          ) {
            dispatch({ type: "UPDATE_CLIENT", payload: data.lock });
          }
        }
      },
    }
  );
  return (
    <div>
      {connectionLost.open ? (
        <ConnectionLost
          open={connectionLost.open}
          handleClose={() => setConnectionLost({ open: false, code: null })}
          code={connectionLost.code}
        />
      ) : null}
      {openMailOnResearchDialog ? (
        <JoinResearchDialog
          open={openMailOnResearchDialog}
          handleClose={() => setOpenMailOnResearchDialog(false)}
        />
      ) : null}
    </div>
  );
}

export default Poll;
